import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { isBoard } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import ProjectsExport from '../components/ProjectsExport';
import { ToastContainer, ToastType } from '../components/Toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  XCircleIcon,
  ChartBarIcon,
  LinkIcon,
  PhotoIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

// === Story-Hero-Card Constants ===
const CARD_BG = '#1c2432';

// Interfaces
interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
  created_at: string;
  creator_name: string;
  image_path?: string;
  voting_deadline?: string;
  min_price?: number;
  max_price?: number;
  variant_count?: number;
  helper_count?: number;
  items?: ProjectItem[];
}

interface ProjectItem {
  id: number;
  project_id: number;
  name: string;
  description: string;
  quantity: number;
  position_order: number;
  variants?: ItemVariant[];
}

interface ItemVariant {
  id: number;
  project_item_id: number;
  name: string;
  description: string;
  price: number | string;
  image_path?: string;
  link?: string;
  vote_count: number;
  total_votes: number;
  my_vote?: number;
  creator_name?: string;
}

interface Vote {
  id: number;
  variant_id: number;
  member_id: number;
  vote_value: number;
  comment: string;
  voter_name: string;
  created_at: string;
  updated_at: string;
}

interface ProjectHelper {
  id: number;
  project_id: number;
  member_id: number;
  message: string | null;
  created_at: string;
  member_name: string;
  email: string;
  tel_nummer: string;
}

const ProjectsManagement: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const isBoardMember = isBoard(user);

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);

  // Toast-System
  const [toasts, setToasts] = useState<Array<{ id: string; type: ToastType; message: string }>>([]);

  const addToast = (type: ToastType, message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Grid Layout State - Anzahl der Spalten für Varianten-Ansicht (2-6)
  const [gridColumns, setGridColumns] = useState<number>(() => {
    const saved = localStorage.getItem('projectsGridColumns');
    return saved ? parseInt(saved, 10) : 3;
  });

  const handleGridColumnsChange = (columns: number) => {
    setGridColumns(columns);
    localStorage.setItem('projectsGridColumns', columns.toString());
  };

  // Grid class helper
  const getGridClass = (): string => {
    const baseClass = 'grid grid-cols-1';

    // Tablet: max 3 Spalten
    let mdClass = 'md:grid-cols-2';
    if (gridColumns === 3) mdClass = 'md:grid-cols-3';

    // Desktop: volle Kontrolle, aber 5-6 Spalten erst ab xl
    let lgClass = '';
    switch(gridColumns) {
      case 2: lgClass = 'lg:grid-cols-2'; break;
      case 3: lgClass = 'lg:grid-cols-3'; break;
      case 4: lgClass = 'lg:grid-cols-4'; break;
      case 5: lgClass = 'lg:grid-cols-4 xl:grid-cols-5'; break;
      case 6: lgClass = 'lg:grid-cols-4 xl:grid-cols-6'; break;
      default: lgClass = 'lg:grid-cols-3';
    }

    return `${baseClass} ${mdClass} ${lgClass}`;
  };

  // Dynamisches Card-Spacing basierend auf Spaltenanzahl
  const getCardSpacing = (): string => {
    if (gridColumns <= 3) return 'p-5';
    if (gridColumns === 4) return 'p-4';
    return 'p-3'; // 5-6 Spalten
  };

  // Dynamischer Grid-Gap
  const getGridGap = (): string => {
    if (gridColumns <= 3) return 'gap-6';
    if (gridColumns === 4) return 'gap-4';
    return 'gap-3'; // 5-6 Spalten
  };

  // Dynamische Bild-Höhe
  const getImageHeight = (): string => {
    if (gridColumns <= 3) return 'h-56';
    if (gridColumns === 4) return 'h-48';
    return 'h-40'; // 5-6 Spalten
  };

  // Dynamische Text-Größen
  const getTextSizes = () => ({
    name: gridColumns <= 4 ? 'text-lg' : 'text-base',
    price: gridColumns <= 4 ? 'text-lg' : 'text-base',
    description: gridColumns <= 4 ? 'text-sm' : 'text-xs',
  });

  // Helper function to check if voting deadline has passed
  const isVotingClosed = (deadline?: string): boolean => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  // Helper function to format remaining time
  const getRemainingTime = (deadline?: string): string => {
    if (!deadline) return '';

    const now = new Date();
    const end = new Date(deadline);
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Abstimmung beendet';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Noch ${days} Tag${days > 1 ? 'e' : ''} und ${hours} Stunden`;
    if (hours > 0) return `Noch ${hours} Stunde${hours > 1 ? 'n' : ''} und ${minutes} Minuten`;
    return `Noch ${minutes} Minute${minutes > 1 ? 'n' : ''}`;
  };
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showVotingHistoryModal, setShowVotingHistoryModal] = useState(false);
  const [showHelpersModal, setShowHelpersModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProjectItem | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ItemVariant | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingItem, setEditingItem] = useState<ProjectItem | null>(null);
  const [editingVariant, setEditingVariant] = useState<ItemVariant | null>(null);
  const [votingHistory, setVotingHistory] = useState<Vote[]>([]);
  const [helpers, setHelpers] = useState<ProjectHelper[]>([]);

  // Form states
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    status: 'draft',
    voting_deadline: ''
  });

  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    quantity: 1,
    position_order: 0
  });

  const [variantForm, setVariantForm] = useState({
    name: '',
    description: '',
    price: 0,
    link: ''
  });

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedProjectImage, setSelectedProjectImage] = useState<File | null>(null);
  const [projectImagePreview, setProjectImagePreview] = useState<string | null>(null);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    visible: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load projects
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/projects');
      if (response.data.success) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Projekte:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async (projectId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}`);
      if (response.data.success) {
        setSelectedProject(response.data.project);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
    }
  };

  // Item nach oben verschieben
  const moveItemUp = async (index: number) => {
    if (!selectedProject || !selectedProject.items || index === 0) return;

    const items = [...selectedProject.items];
    const temp = items[index];
    items[index] = items[index - 1];
    items[index - 1] = temp;

    // Update position_order values
    const reorderedItems = items.map((item, idx) => ({
      id: item.id,
      position_order: idx,
    }));

    try {
      const response = await apiClient.put(`/api/projects/${selectedProject.id}/items/reorder`, {
        items: reorderedItems,
      });

      if (response.data.success) {
        loadProjectDetails(selectedProject.id);
        addToast('success', 'Reihenfolge erfolgreich geändert');
      }
    } catch (error: any) {
      console.error('Fehler beim Verschieben:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Verschieben';
      addToast('error', errorMessage);
    }
  };

  // Item nach unten verschieben
  const moveItemDown = async (index: number) => {
    if (!selectedProject || !selectedProject.items || index === selectedProject.items.length - 1) return;

    const items = [...selectedProject.items];
    const temp = items[index];
    items[index] = items[index + 1];
    items[index + 1] = temp;

    // Update position_order values
    const reorderedItems = items.map((item, idx) => ({
      id: item.id,
      position_order: idx,
    }));

    try {
      const response = await apiClient.put(`/api/projects/${selectedProject.id}/items/reorder`, {
        items: reorderedItems,
      });

      if (response.data.success) {
        loadProjectDetails(selectedProject.id);
        addToast('success', 'Reihenfolge erfolgreich geändert');
      }
    } catch (error: any) {
      console.error('Fehler beim Verschieben:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Verschieben';
      addToast('error', errorMessage);
    }
  };

  // Project CRUD
  const handleSaveProject = async () => {
    try {
      const formData = new FormData();
      formData.append('name', projectForm.name);
      formData.append('description', projectForm.description);
      formData.append('status', projectForm.status);

      if (projectForm.voting_deadline) {
        formData.append('voting_deadline', projectForm.voting_deadline);
      }

      if (selectedProjectImage) {
        formData.append('image', selectedProjectImage);
      }

      if (editingProject) {
        // Update existing project
        const response = await apiClient.put(`/api/projects/${editingProject.id}`, formData);
        if (response.data.success) {
          loadProjects();
          if (selectedProject?.id === editingProject.id) {
            loadProjectDetails(editingProject.id);
          }
          setShowProjectModal(false);
          setEditingProject(null);
          setProjectForm({ name: '', description: '', status: 'draft', voting_deadline: '' });
          setSelectedProjectImage(null);
          setProjectImagePreview(null);
          addToast('success', 'Projekt erfolgreich aktualisiert');
        }
      } else {
        // Create new project
        const response = await apiClient.post('/api/projects', formData);
        if (response.data.success) {
          loadProjects();
          setShowProjectModal(false);
          setProjectForm({ name: '', description: '', status: 'draft', voting_deadline: '' });
          setSelectedProjectImage(null);
          setProjectImagePreview(null);
          addToast('success', 'Projekt erfolgreich erstellt');
        }
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern des Projekts:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Speichern des Projekts';
      addToast('error', errorMessage);
    }
  };

  const openEditProject = (project: Project) => {
    setEditingProject(project);

    // Format voting_deadline for datetime-local input
    let formattedDeadline = '';
    if (project.voting_deadline) {
      const deadline = new Date(project.voting_deadline);
      formattedDeadline = deadline.toISOString().slice(0, 16);
    }

    setProjectForm({
      name: project.name,
      description: project.description,
      status: project.status,
      voting_deadline: formattedDeadline,
    });
    // Load existing image if available
    if (project.image_path) {
      setProjectImagePreview(getImageUrl(project.image_path) || null);
    } else {
      setProjectImagePreview(null);
    }
    setSelectedProjectImage(null);
    setShowProjectModal(true);
  };

  const openNewProject = () => {
    setEditingProject(null);
    setProjectForm({ name: '', description: '', status: 'draft', voting_deadline: '' });
    setSelectedProjectImage(null);
    setProjectImagePreview(null);
    setShowProjectModal(true);
  };

  const handleProjectImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedProjectImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProjectImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProjectPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          const timestamp = new Date().getTime();
          const renamedFile = new File([file], `pasted-project-${timestamp}.png`, { type: file.type });

          setSelectedProjectImage(renamedFile);

          const reader = new FileReader();
          reader.onloadend = () => {
            setProjectImagePreview(reader.result as string);
          };
          reader.readAsDataURL(renamedFile);

          addToast('success', 'Bild aus Zwischenablage eingefügt!');
        }
        break;
      }
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    try {
      const response = await apiClient.delete(`/api/projects/${projectId}`);
      if (response.data.success) {
        loadProjects();
        setSelectedProject(null);
        addToast('success', 'Projekt erfolgreich gelöscht');
      }
    } catch (error: any) {
      console.error('Fehler beim Löschen des Projekts:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen des Projekts';
      addToast('error', errorMessage);
    }
  };

  const confirmDeleteProject = (projectId: number, projectName: string) => {
    setConfirmationModal({
      visible: true,
      title: 'Projekt löschen',
      message: `Möchten Sie das Projekt "${projectName}" wirklich löschen? Alle zugehörigen Items, Varianten und Abstimmungen werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`,
      onConfirm: () => {
        handleDeleteProject(projectId);
        setConfirmationModal({ ...confirmationModal, visible: false });
      },
    });
  };

  // Item CRUD
  const handleSaveItem = async () => {
    if (!selectedProject) return;

    try {
      if (editingItem) {
        // Update existing item
        const response = await apiClient.put(`/api/projects/items/${editingItem.id}`, itemForm);
        if (response.data.success) {
          loadProjectDetails(selectedProject.id);
          setShowItemModal(false);
          setEditingItem(null);
          setItemForm({ name: '', description: '', quantity: 1, position_order: 0 });
          addToast('success', 'Position erfolgreich aktualisiert');
        }
      } else {
        // Create new item
        const response = await apiClient.post(`/api/projects/${selectedProject.id}/items`, itemForm);
        if (response.data.success) {
          loadProjectDetails(selectedProject.id);
          setShowItemModal(false);
          setItemForm({ name: '', description: '', quantity: 1, position_order: 0 });
          addToast('success', 'Position erfolgreich erstellt');
        }
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern der Position:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Speichern der Position';
      addToast('error', errorMessage);
    }
  };

  const openEditItem = (item: ProjectItem) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      description: item.description,
      quantity: item.quantity,
      position_order: item.position_order,
    });
    setShowItemModal(true);
  };

  const openNewItem = () => {
    setEditingItem(null);
    setItemForm({ name: '', description: '', quantity: 1, position_order: 0 });
    setShowItemModal(true);
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      const response = await apiClient.delete(`/api/projects/items/${itemId}`);
      if (response.data.success && selectedProject) {
        loadProjectDetails(selectedProject.id);
      }
    } catch (error: any) {
      console.error('Fehler beim Löschen der Position:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen der Position';
      addToast('error', errorMessage);
    }
  };

  const confirmDeleteItem = (itemId: number, itemName: string) => {
    setConfirmationModal({
      visible: true,
      title: 'Position löschen',
      message: `Möchten Sie die Position "${itemName}" wirklich löschen? Alle zugehörigen Varianten und Abstimmungen werden ebenfalls gelöscht.`,
      onConfirm: () => {
        handleDeleteItem(itemId);
        setConfirmationModal({ ...confirmationModal, visible: false });
      },
    });
  };

  // Variant CRUD
  const handleSaveVariant = async () => {
    if (!selectedItem) return;

    try {
      const formData = new FormData();
      formData.append('name', variantForm.name);
      formData.append('description', variantForm.description);
      formData.append('price', variantForm.price.toString());
      formData.append('link', variantForm.link);

      if (selectedImage) {
        formData.append('image', selectedImage);
      } else if (editingVariant && !imagePreview) {
        // Bild wurde entfernt
        formData.append('keep_image', 'false');
      }

      let response;
      if (editingVariant) {
        // Update existing variant
        response = await apiClient.put(`/api/projects/variants/${editingVariant.id}`, formData);
      } else {
        // Create new variant
        response = await apiClient.post(`/api/projects/items/${selectedItem.id}/variants`, formData);
      }

      if (response.data.success && selectedProject) {
        loadProjectDetails(selectedProject.id);
        setShowVariantModal(false);
        setEditingVariant(null);
        setVariantForm({ name: '', description: '', price: 0, link: '' });
        setSelectedImage(null);
        setImagePreview(null);
        addToast('success', editingVariant ? 'Variante erfolgreich aktualisiert' : 'Variante erfolgreich erstellt');
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern der Variante:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Speichern der Variante';
      addToast('error', errorMessage);
    }
  };

  const openEditVariant = (variant: ItemVariant, item: ProjectItem) => {
    setEditingVariant(variant);
    setSelectedItem(item);
    setVariantForm({
      name: variant.name,
      description: variant.description,
      price: typeof variant.price === 'string' ? parseFloat(variant.price) : variant.price,
      link: variant.link || '',
    });
    // Set image preview if variant has an image
    if (variant.image_path) {
      setImagePreview(getImageUrl(variant.image_path) || null);
    } else {
      setImagePreview(null);
    }
    setSelectedImage(null);
    setShowVariantModal(true);
  };

  const openNewVariant = (item: ProjectItem) => {
    setEditingVariant(null);
    setSelectedItem(item);
    setVariantForm({ name: '', description: '', price: 0, link: '' });
    setSelectedImage(null);
    setImagePreview(null);
    setShowVariantModal(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          // Create a new File with a proper name
          const timestamp = new Date().getTime();
          const renamedFile = new File([file], `pasted-image-${timestamp}.png`, { type: file.type });

          setSelectedImage(renamedFile);

          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreview(reader.result as string);
          };
          reader.readAsDataURL(renamedFile);

          // Show feedback
          addToast('success', 'Bild aus Zwischenablage eingefügt!');
        }
        break;
      }
    }
  };

  const handleDeleteVariant = async (variantId: number) => {
    try {
      const response = await apiClient.delete(`/api/projects/variants/${variantId}`);
      if (response.data.success && selectedProject) {
        loadProjectDetails(selectedProject.id);
      }
    } catch (error: any) {
      console.error('Fehler beim Löschen der Variante:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen der Variante';
      addToast('error', errorMessage);
    }
  };

  const confirmDeleteVariant = (variantId: number, variantName: string) => {
    setConfirmationModal({
      visible: true,
      title: 'Variante löschen',
      message: `Möchten Sie die Variante "${variantName}" wirklich löschen? Alle zugehörigen Abstimmungen werden ebenfalls gelöscht.`,
      onConfirm: () => {
        handleDeleteVariant(variantId);
        setConfirmationModal({ ...confirmationModal, visible: false });
      },
    });
  };

  // Voting
  const handleVote = async (variantId: number, voteValue: number) => {
    try {
      const response = await apiClient.post(`/api/projects/variants/${variantId}/vote`, { vote_value: voteValue });
      if (response.data.success && selectedProject) {
        loadProjectDetails(selectedProject.id);
      }
    } catch (error) {
      console.error('Fehler beim Abstimmen:', error);
    }
  };

  // Voting-Historie laden
  const loadVotingHistory = async (variantId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/variants/${variantId}/votes`);
      if (response.data.success) {
        setVotingHistory(response.data.votes);
        setShowVotingHistoryModal(true);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Voting-Historie:', error);
      addToast('error', 'Fehler beim Laden der Abstimmungshistorie');
    }
  };

  // Projekt-Helfer laden
  const loadHelpers = async (projectId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/helpers`);
      if (response.data.success) {
        setHelpers(response.data.helpers);
        setShowHelpersModal(true);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Helfer:', error);
      addToast('error', 'Fehler beim Laden der Helfer-Liste');
    }
  };

  // Hilfsfunktion um String oder Number zu Number zu konvertieren
  const toNumber = (value: string | number | undefined | null): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const getTotalPrice = (item: ProjectItem): number => {
    if (!item.variants || item.variants.length === 0) return 0;
    const topVariant = item.variants[0];
    return toNumber(topVariant.price) * item.quantity;
  };

  const getImageUrl = (imagePath: string | undefined): string | undefined => {
    if (!imagePath) return undefined;
    // If it's already a full URL, return it
    if (imagePath.startsWith('http')) return imagePath;
    // Use the new project image endpoint
    return `${BASE_URL}/api/projects/image/${encodeURIComponent(imagePath)}`;
  };

  // Stats für Hero
  const monthYear = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const activeProjects = projects.filter(p => p.status === 'active').length;
  const draftProjects = projects.filter(p => p.status === 'draft').length;
  const totalVariants = projects.reduce((sum, p) => sum + (p.variant_count ?? 0), 0);
  const totalHelpers = projects.reduce((sum, p) => sum + (p.helper_count ?? 0), 0);

  return (
    <>
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero Section - only for list view */}
        {!selectedProject && (
          <section className="pt-2 pb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
              Verwaltung · Stand {monthYear}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
              Vereins-<br />
              <span className="text-amber-600 dark:text-amber-400">Werkbank.</span>
            </h1>
            <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
              {projects.length === 0 ? (
                <>Noch keine Projekte. Lege das <span className="font-bold text-gray-900 dark:text-white">erste Vereinsprojekt</span> mit Stücklisten und Abstimmung an.</>
              ) : (
                <>
                  <span className="font-bold text-gray-900 dark:text-white">{projects.length} {projects.length === 1 ? 'Projekt' : 'Projekte'}</span>
                  {activeProjects > 0 && <> · <span className="font-bold text-emerald-700 dark:text-emerald-300">{activeProjects} aktiv</span></>}
                  {draftProjects > 0 && <> · <span className="font-bold text-amber-700 dark:text-amber-300">{draftProjects} {draftProjects === 1 ? 'Entwurf' : 'Entwürfe'}</span></>}
                  {totalVariants > 0 && <> · <span className="font-bold text-gray-900 dark:text-white">{totalVariants} Varianten zur Wahl</span></>}
                  .
                </>
              )}
            </p>
          </section>
        )}

        {/* Action Banner - only for list view if board */}
        {!selectedProject && projects.length > 0 && isBoardMember && (
          <section className="pb-6">
            <article className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0 w-full">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)' }}>
                    <FolderIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Vorstands-Aufgabe</div>
                    <h4 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Neues Projekt anlegen</h4>
                    <p className="text-sm text-gray-700 dark:text-slate-300 mt-2">Bündle Anschaffungen, lass Mitglieder über Varianten abstimmen und sammle Helfer ein.</p>
                  </div>
                </div>
                <button
                  onClick={openNewProject}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition whitespace-nowrap"
                >
                  <PlusIcon className="h-5 w-5" />
                  Neues Projekt
                </button>
              </div>
            </article>
          </section>
        )}

        {/* Projects List */}
        {!selectedProject ? (
          projects.length === 0 ? (
            /* Empty State */
            <div className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}>
              <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                <FolderIcon className="h-10 w-10 text-amber-600 dark:text-amber-400" />
              </div>
              <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Noch keine Projekte</h4>
              <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                Erstelle dein erstes Vereinsprojekt mit Stücklisten und Abstimmungen, um die Mitglieder einzubinden.
              </p>
              {isBoardMember && (
                <button
                  onClick={openNewProject}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition"
                >
                  <PlusIcon className="h-5 w-5" />
                  Projekt erstellen
                </button>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="rounded-3xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.01] bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                onClick={() => loadProjectDetails(project.id)}
              >
                {/* Project Image */}
                <div className="relative h-56 overflow-hidden bg-gray-50 dark:bg-slate-900/60" >
                  {project.image_path ? (
                    <>
                      <img
                        src={getImageUrl(project.image_path)}
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          const fallback = img.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                        <FolderIcon className="h-24 w-24 text-slate-600" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderIcon className="h-24 w-24 text-slate-600" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4 gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                        {project.name}
                      </h3>
                      <p className="mt-2 text-gray-700 dark:text-slate-300 line-clamp-2">
                        {project.description}
                      </p>
                    </div>
                    {isBoardMember && (
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditProject(project);
                          }}
                          className="p-2 rounded-lg text-gray-700 dark:text-slate-300 hover:text-white transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                          title="Projekt bearbeiten"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDeleteProject(project.id, project.name);
                          }}
                          className="p-2 rounded-lg text-rose-700 dark:text-rose-300 hover:text-rose-200 transition-colors"
                          style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                          title="Projekt löschen"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Price Range */}
                  {(project.variant_count ?? 0) > 0 && (
                    <div className="rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-slate-700" style={{ background: 'rgba(15,23,42,0.4)' }}>
                      <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-2">
                        <span>Preisspanne</span>
                        <span className="text-xs">
                          {project.variant_count} {project.variant_count === 1 ? 'Variante' : 'Varianten'}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
                        {project.min_price === project.max_price ? (
                          <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tabular-nums">
                            {toNumber(project.min_price).toFixed(2)} €
                          </span>
                        ) : (
                          <>
                            <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                              {toNumber(project.min_price).toFixed(2)} €
                            </span>
                            <span className="text-gray-500 dark:text-slate-500">—</span>
                            <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                              {toNumber(project.max_price).toFixed(2)} €
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                      project.status === 'active'
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                        : project.status === 'draft'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                        : 'bg-slate-950/55 border-white/10 text-slate-300'
                    }`}>
                      {project.status === 'active' ? 'Aktiv' : project.status === 'draft' ? 'Entwurf' : 'Archiviert'}
                    </span>
                    {project.voting_deadline && (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                        isVotingClosed(project.voting_deadline)
                          ? 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                          : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                      }`}>
                        {getRemainingTime(project.voting_deadline)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          )
        ) : (
          /* Project Details View */
          <div className="space-y-6">
            {/* Back Button */}
            <button
              onClick={() => setSelectedProject(null)}
              className="inline-flex items-center gap-2 px-3 py-2 -ml-2 rounded-lg text-amber-700 dark:text-amber-300 hover:text-amber-200 font-semibold text-sm transition-colors"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              ← <span className="hidden sm:inline">Zurück zur Übersicht</span><span className="sm:hidden">Zurück</span>
            </button>

            {/* Project Header */}
            <div className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden bg-gray-50 dark:bg-slate-900/60" >
                {selectedProject.image_path ? (
                  <>
                    <img
                      src={getImageUrl(selectedProject.image_path)}
                      alt={selectedProject.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                      <FolderIcon className="h-32 w-32 text-slate-600" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderIcon className="h-32 w-32 text-slate-600" />
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-6">
                <div className="flex-1 mb-4">
                  <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                    {selectedProject.name}
                  </h2>
                  <p className="mt-2 text-sm sm:text-base text-gray-700 dark:text-slate-300">
                    {selectedProject.description}
                  </p>
                  <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-slate-500">
                    Erstellt von: {selectedProject.creator_name}
                  </p>
                  {selectedProject.voting_deadline && (
                    <div className={`mt-4 inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm border ${
                      isVotingClosed(selectedProject.voting_deadline)
                        ? 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                        : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                    }`}>
                      <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                      <span className="font-semibold">{getRemainingTime(selectedProject.voting_deadline)}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-800 dark:text-slate-200 transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    title="Als PDF exportieren"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 flex-shrink-0" />
                    <span>PDF Export</span>
                  </button>
                  {isBoardMember && (
                    <button
                      onClick={() => loadHelpers(selectedProject.id)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-800 dark:text-slate-200 transition-colors relative bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                      title="Helfer anzeigen"
                    >
                      <UserGroupIcon className="h-5 w-5 flex-shrink-0" />
                      <span>Helfer</span>
                      {(selectedProject.helper_count ?? 0) > 0 && (
                        <span className="absolute -top-2 -right-2 bg-amber-500 text-slate-950 text-xs font-black rounded-full h-6 w-6 flex items-center justify-center">
                          {selectedProject.helper_count}
                        </span>
                      )}
                    </button>
                  )}
                  {isBoardMember && (
                    <>
                      <button
                        onClick={() => openEditProject(selectedProject)}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-800 dark:text-slate-200 transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        title="Projekt bearbeiten"
                      >
                        <PencilIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Bearbeiten</span>
                      </button>
                      <button
                        onClick={openNewItem}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition"
                        title="Neue Position hinzufügen"
                      >
                        <PlusIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Position hinzufügen</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Items List */}
            {selectedProject.items && selectedProject.items.map((item, index) => (
              <div key={item.id} className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <ClipboardDocumentListIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                      {item.name}
                      <span className="text-sm font-normal text-gray-600 dark:text-slate-400">
                        ({item.quantity}x)
                      </span>
                    </h3>
                    {item.description && (
                      <p className="mt-1 text-gray-700 dark:text-slate-300">{item.description}</p>
                    )}
                  </div>
                  {isBoardMember && (
                    <div className="flex gap-1 sm:gap-2 flex-wrap shrink-0">
                      {/* Reorder buttons */}
                      <div className="flex gap-1 pr-2" style={{ borderRight: '1px solid #334155' }}>
                        <button
                          onClick={() => moveItemUp(index)}
                          disabled={index === 0}
                          className={`p-2 rounded-lg transition-colors ${
                            index === 0
                              ? 'text-slate-700 cursor-not-allowed'
                              : 'text-slate-400 hover:text-white hover:bg-white/5'
                          }`}
                          title="Nach oben verschieben"
                        >
                          <ChevronUpIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => moveItemDown(index)}
                          disabled={index === selectedProject.items!.length - 1}
                          className={`p-2 rounded-lg transition-colors ${
                            index === selectedProject.items!.length - 1
                              ? 'text-slate-700 cursor-not-allowed'
                              : 'text-slate-400 hover:text-white hover:bg-white/5'
                          }`}
                          title="Nach unten verschieben"
                        >
                          <ChevronDownIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <button
                        onClick={() => openNewVariant(item)}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-gray-800 dark:text-slate-200 transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                      >
                        <PlusIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Variante</span>
                      </button>
                      <button
                        onClick={() => openEditItem(item)}
                        className="p-2 rounded-lg text-gray-700 dark:text-slate-300 hover:text-white transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        title="Position bearbeiten"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => confirmDeleteItem(item.id, item.name)}
                        className="p-2 rounded-lg text-rose-700 dark:text-rose-300 hover:text-rose-200 transition-colors"
                        style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                        title="Position löschen"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Variants */}
                {item.variants && item.variants.length > 0 && (
                  <>
                    {/* Grid Layout Controls */}
                    <div className="mb-6 hidden md:flex items-center gap-3 justify-end">
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-300 hidden sm:inline">Ansicht:</span>
                      <div className="inline-flex gap-1.5 rounded-xl p-1.5 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700" >
                        {[2, 3, 4, 5, 6].map((cols) => (
                          <button
                            key={cols}
                            onClick={() => handleGridColumnsChange(cols)}
                            className={`min-w-[2.5rem] px-3 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
                              gridColumns === cols
                                ? 'bg-amber-500 text-slate-950 scale-105'
                                : 'text-slate-400 hover:bg-white/5 hover:text-white'
                            }`}
                            title={`${cols} Spalten anzeigen`}
                            aria-label={`${cols} Spalten anzeigen`}
                            aria-pressed={gridColumns === cols}
                          >
                            {cols}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className={`${getGridClass()} ${getGridGap()}`}>
                    {item.variants.map((variant) => {
                      const textSizes = getTextSizes();
                      return (
                      <div
                        key={variant.id}
                        className="relative rounded-2xl transition-all duration-300 overflow-hidden group border border-gray-200 dark:border-slate-700"
                        style={{ background: 'rgba(15,23,42,0.4)' }}
                      >
                        {/* Image or Placeholder */}
                        <div className={`bg-gray-50 dark:bg-slate-900/60 ${`relative ${getImageHeight()} overflow-hidden`}`} >
                          {variant.image_path ? (
                            <>
                              <img
                                src={getImageUrl(variant.image_path)}
                                alt={variant.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  const img = e.target as HTMLImageElement;
                                  img.style.display = 'none';
                                  const fallback = img.nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                              <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                                <PhotoIcon className="h-20 w-20 text-slate-600" />
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <PhotoIcon className="h-20 w-20 text-slate-600" />
                            </div>
                          )}

                          {/* Vote Badge */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadVotingHistory(variant.id);
                            }}
                            className="absolute top-3 right-3 backdrop-blur-md px-3 py-1.5 rounded-full transition-all hover:scale-105 cursor-pointer border border-gray-200 dark:border-white/10"
                            style={{ background: 'rgba(2,6,23,0.70)' }}
                            title="Abstimmungshistorie anzeigen"
                          >
                            <div className="flex items-center gap-1.5">
                              <ChartBarIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                              <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                {variant.total_votes}
                              </span>
                            </div>
                          </button>
                        </div>

                        {/* Content */}
                        <div className={getCardSpacing()}>
                          <h4 className={`${textSizes.name} font-bold text-white mb-2 line-clamp-1`}>
                            {variant.name}
                          </h4>

                          {variant.creator_name && (
                            <div className="mb-2">
                              <span className={`inline-flex items-center rounded-full font-medium bg-amber-500/10 border border-amber-500/30 text-amber-300 ${
                                gridColumns <= 4 ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.5 text-[10px]'
                              }`}>
                                {gridColumns <= 4 ? `Von ${variant.creator_name}` : variant.creator_name}
                              </span>
                            </div>
                          )}

                          {gridColumns <= 4 && variant.description && (
                            <p className={`${textSizes.description} text-slate-300 mb-3 line-clamp-2`}>
                              {variant.description}
                            </p>
                          )}

                          {/* Price Section */}
                          <div className={`rounded-xl mb-3 ${gridColumns <= 4 ? 'p-3' : 'p-2'}`} style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                            <div className="flex items-baseline gap-1.5">
                              <span className={`${textSizes.price} font-black text-amber-300 tabular-nums`}>
                                {toNumber(variant.price).toFixed(2)} €
                              </span>
                              <span className="text-xs text-gray-600 dark:text-slate-400">
                                pro Stück
                              </span>
                            </div>
                            <div className="text-sm text-gray-700 dark:text-slate-300 mt-1">
                              Gesamt: <span className="font-bold text-gray-900 dark:text-white tabular-nums">{(toNumber(variant.price) * item.quantity).toFixed(2)} €</span>
                            </div>
                          </div>

                          {/* Link */}
                          {variant.link && (
                            <a
                              href={variant.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-200 font-medium mb-4 group/link"
                            >
                              <LinkIcon className="h-4 w-4 group-hover/link:translate-x-0.5 transition-transform" />
                              Link öffnen
                            </a>
                          )}

                          {/* Voting Buttons */}
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => handleVote(variant.id, 1)}
                              className={`flex-1 flex items-center justify-center rounded-xl transition-all duration-200 ${
                                gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3'
                              } ${
                                variant.my_vote === 1
                                  ? 'bg-emerald-500 text-slate-950 scale-[1.02]'
                                  : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-emerald-500/20 hover:text-emerald-300 hover:scale-[1.02]'
                              }`}
                              title="Dafür stimmen"
                              aria-label="Für diese Variante stimmen"
                              aria-pressed={variant.my_vote === 1}
                            >
                              <HandThumbUpIcon className={gridColumns <= 4 ? 'h-6 w-6' : 'h-5 w-5'} />
                            </button>
                            <button
                              onClick={() => handleVote(variant.id, -1)}
                              className={`flex-1 flex items-center justify-center rounded-xl transition-all duration-200 ${
                                gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3'
                              } ${
                                variant.my_vote === -1
                                  ? 'bg-rose-500 text-white scale-[1.02]'
                                  : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-rose-500/20 hover:text-rose-300 hover:scale-[1.02]'
                              }`}
                              title="Dagegen stimmen"
                              aria-label="Gegen diese Variante stimmen"
                              aria-pressed={variant.my_vote === -1}
                            >
                              <HandThumbDownIcon className={gridColumns <= 4 ? 'h-6 w-6' : 'h-5 w-5'} />
                            </button>
                          </div>

                          {/* Edit and Delete Buttons (Board only) */}
                          {isBoardMember && (
                            <div className="mt-3 flex gap-2">
                              <button
                                onClick={() => openEditVariant(variant, item)}
                                className="flex-1 py-2 text-xs text-amber-700 dark:text-amber-300 hover:text-amber-200 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                                style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.20)' }}
                                title="Variante bearbeiten"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                                Bearbeiten
                              </button>
                              <button
                                onClick={() => confirmDeleteVariant(variant.id, variant.name)}
                                className="flex-1 py-2 text-xs text-rose-700 dark:text-rose-300 hover:text-rose-200 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                                style={{ background: 'rgba(244,63,94,0.05)', border: '1px solid rgba(244,63,94,0.20)' }}
                                title="Variante löschen"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                                Löschen
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-surface-dark rounded-t-2xl sm:rounded-lg max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingProject ? 'Projekt bearbeiten' : 'Neues Projekt'}
              </h2>
              {!editingProject && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Neue Projekte werden als <span className="font-semibold text-warning-600 dark:text-warning-400">Entwurf</span> erstellt und sind für Mitglieder nicht sichtbar.
                </p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Projektname
                </label>
                <input
                  type="text"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="z.B. Renovierung Vereinsheim"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  className="input w-full h-32"
                  placeholder="Projektbeschreibung..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Abstimmungs-Deadline (optional)
                </label>
                <input
                  type="datetime-local"
                  value={projectForm.voting_deadline}
                  onChange={(e) => setProjectForm({ ...projectForm, voting_deadline: e.target.value })}
                  className="input w-full"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Nach diesem Zeitpunkt können Mitglieder nicht mehr abstimmen
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Projekt-Bild (optional)
                </label>

                {/* Paste Area */}
                <div
                  onPaste={handleProjectPaste}
                  tabIndex={0}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 mb-3 text-center cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <span className="font-semibold text-primary-600 dark:text-primary-400">
                      Klicken Sie hier und drücken Sie Strg+V
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    um ein Bild aus der Zwischenablage einzufügen
                  </p>
                </div>

                {/* File Input */}
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                  oder
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProjectImageChange}
                  className="input w-full"
                />

                {/* Preview */}
                {projectImagePreview && (
                  <div className="mt-4 relative">
                    <img
                      src={projectImagePreview}
                      alt="Vorschau"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => {
                        setSelectedProjectImage(null);
                        setProjectImagePreview(null);
                      }}
                      className="absolute top-2 right-2 p-2 bg-error-500 hover:bg-error-600 text-gray-900 dark:text-white rounded-full transition-colors"
                      title="Bild entfernen"
                      type="button"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
              {editingProject && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={projectForm.status}
                    onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                    className="input w-full"
                  >
                    <option value="draft">Entwurf (nicht sichtbar für Mitglieder)</option>
                    <option value="active">Aktiv (sichtbar für Mitglieder)</option>
                    <option value="archived">Archiviert</option>
                  </select>
                </div>
              )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setEditingProject(null);
                  setProjectForm({ name: '', description: '', status: 'draft', voting_deadline: '' });
                  setSelectedProjectImage(null);
                  setProjectImagePreview(null);
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveProject}
                className="btn btn-primary"
              >
                {editingProject ? 'Speichern' : 'Projekt erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Modal */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingItem ? 'Position bearbeiten' : 'Neue Position'}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Position
                </label>
                <input
                  type="text"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="z.B. Stühle"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  className="input w-full h-24"
                  placeholder="Beschreibung der Position..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Anzahl
                </label>
                <input
                  type="number"
                  value={itemForm.quantity}
                  onChange={(e) => setItemForm({ ...itemForm, quantity: parseInt(e.target.value) })}
                  className="input w-full"
                  min="1"
                />
              </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowItemModal(false);
                  setEditingItem(null);
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveItem}
                className="btn btn-primary"
              >
                {editingItem ? 'Speichern' : 'Position hinzufügen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variant Modal */}
      {showVariantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-surface-dark rounded-t-2xl sm:rounded-lg max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingVariant ? 'Variante bearbeiten' : 'Neue Variante'}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Variante
                </label>
                <input
                  type="text"
                  value={variantForm.name}
                  onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="z.B. Holzstuhl Classic"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={variantForm.description}
                  onChange={(e) => setVariantForm({ ...variantForm, description: e.target.value })}
                  className="input w-full h-24"
                  placeholder="Beschreibung der Variante..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Preis (pro Stück)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={variantForm.price}
                  onChange={(e) => setVariantForm({ ...variantForm, price: parseFloat(e.target.value) })}
                  className="input w-full"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bild hochladen (optional)
                </label>

                {/* Paste Area */}
                <div
                  onPaste={handlePaste}
                  tabIndex={0}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 mb-3 text-center cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <span className="font-semibold text-primary-600 dark:text-primary-400">
                      Klicken Sie hier und drücken Sie Strg+V
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    um ein Bild aus der Zwischenablage einzufügen
                  </p>
                </div>

                {/* File Input */}
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                  oder
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="input w-full"
                />

                {/* Preview */}
                {imagePreview && (
                  <div className="mt-4 relative">
                    <img
                      src={imagePreview}
                      alt="Vorschau"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 p-2 bg-error-500 hover:bg-error-600 text-gray-900 dark:text-white rounded-full transition-colors"
                      title="Bild entfernen"
                      type="button"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link (optional)
                </label>
                <input
                  type="text"
                  value={variantForm.link}
                  onChange={(e) => setVariantForm({ ...variantForm, link: e.target.value })}
                  className="input w-full"
                  placeholder="https://..."
                />
              </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowVariantModal(false);
                  setEditingVariant(null);
                  setSelectedItem(null);
                  setSelectedImage(null);
                  setImagePreview(null);
                  setVariantForm({ name: '', description: '', price: 0, link: '' });
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveVariant}
                className="btn btn-primary"
              >
                {editingVariant ? 'Variante aktualisieren' : 'Variante hinzufügen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmationModal.visible}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type="warning"
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmationModal({ ...confirmationModal, visible: false }),
          },
          confirm: {
            text: 'Löschen',
            onPress: confirmationModal.onConfirm,
          },
        }}
      />

      {/* Projects Export Modal */}
      <ProjectsExport
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        project={selectedProject}
      />

      {/* Voting History Modal */}
      {showVotingHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Abstimmungshistorie</h2>
              </div>
              <button
                onClick={() => setShowVotingHistoryModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Schließen"
              >
                <XCircleIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {votingHistory.length === 0 ? (
                <div className="text-center py-12">
                  <UserGroupIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Noch keine Abstimmungen für diese Variante</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {votingHistory.map((vote) => (
                    <div
                      key={vote.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            vote.vote_value === 1
                              ? 'bg-success-100 dark:bg-success-900/30'
                              : 'bg-error-100 dark:bg-error-900/30'
                          }`}>
                            {vote.vote_value === 1 ? (
                              <HandThumbUpIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                            ) : (
                              <HandThumbDownIcon className="h-5 w-5 text-error-600 dark:text-error-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {vote.voter_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(vote.created_at).toLocaleString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          vote.vote_value === 1
                            ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300'
                            : 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300'
                        }`}>
                          {vote.vote_value === 1 ? 'Dafür' : 'Dagegen'}
                        </span>
                      </div>
                      {vote.comment && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 pl-12">
                          {vote.comment}
                        </p>
                      )}
                      {vote.updated_at && vote.updated_at !== vote.created_at && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 pl-12">
                          Aktualisiert: {new Date(vote.updated_at).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowVotingHistoryModal(false)}
                className="btn btn-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Helpers Modal */}
      {showHelpersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Projekt-Helfer</h2>
              </div>
              <button
                onClick={() => setShowHelpersModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Schließen"
              >
                <XCircleIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {helpers.length === 0 ? (
                <div className="text-center py-12">
                  <UserGroupIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Noch keine Helfer für dieses Projekt</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {helpers.map((helper) => (
                    <div
                      key={helper.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white text-lg">
                            {helper.member_name}
                          </p>
                          <div className="mt-2 space-y-1">
                            {helper.email && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">E-Mail:</span>{' '}
                                <a href={`mailto:${helper.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                                  {helper.email}
                                </a>
                              </p>
                            )}
                            {helper.tel_nummer && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">Telefon:</span>{' '}
                                <a href={`tel:${helper.tel_nummer}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                                  {helper.tel_nummer}
                                </a>
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                            Registriert: {new Date(helper.created_at).toLocaleString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      {helper.message && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Nachricht:</span> {helper.message}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowHelpersModal(false)}
                className="btn btn-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ProjectsManagement;
